import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { CheckCircle, CircleNotchIcon, Warning } from '@phosphor-icons/react'
import type { PulsePergunta } from '@/components/pulse/pulse-shared'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { getPublicPulsePreview } from '@/api/pulse-carteira/pulse-carteira.actions'
import type { PulsePublicPreviewData } from '@/api/pulse-carteira/pulse-carteira.schema'
import {
  ConfirmacaoStep,
  PesquisaWizard,
  type PulseConfirmacaoData,
} from '@/components/pulse/pulse-shared'
import { getApiErrorMessage } from '@/lib/api-error'

export const Route = createFileRoute('/pulse/preview/$token')({
  component: PulsePreviewPage,
})

function PulsePreviewPage() {
  const { token } = Route.useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<PulsePublicPreviewData | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [step, setStep] = useState<'confirmacao' | 'pesquisa' | 'obrigado'>('confirmacao')
  const [resposta, setResposta] = useState<string | null>(null)
  const [respostasPesquisa, setRespostasPesquisa] = useState<Record<string, any>>({})

  useEffect(() => {
    loadData()
  }, [token])

  async function loadData() {
    setIsLoading(true)
    setError(null)
    try {
      const json = await getPublicPulsePreview(token)
      setData(json.data)
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao carregar dados'))
    } finally {
      setIsLoading(false)
    }
  }

  function handleConfirmacao(resp: string) {
    setResposta(resp)
    setStep('pesquisa')
  }

  function handleSubmitPesquisa() {
    setStep('obrigado')
  }

  const perguntasAtivas = useMemo(() => {
    if (!data?.perguntas) return []
    return (data.perguntas as Array<PulsePergunta>)
      .filter((p) => p.ativa !== false)
      .filter((p) => {
        if (!p.exibirQuando || p.exibirQuando.length === 0) return true
        return p.exibirQuando.includes(resposta as any)
      })
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
  }, [data?.perguntas, resposta])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <CircleNotchIcon className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Warning className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-slate-900 mb-2">Link inválido ou expirado</p>
            <p className="text-sm text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!data) return null

  if (step === 'obrigado') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Preview concluído!</h2>
            <p className="text-slate-600 mb-4">
              Este é um preview do formulário. Em produção, as respostas seriam salvas.
            </p>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <Warning className="h-4 w-4 inline-block mr-1" />
              Modo de teste — nenhum dado foi salvo
            </div>
            <Button className="mt-6" onClick={() => window.close()}>
              Fechar preview
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (step === 'pesquisa') {
    return (
      <PesquisaWizard
        perguntasVisiveis={perguntasAtivas}
        respostasPesquisa={respostasPesquisa}
        setResposta={(perguntaId, valor) =>
          setRespostasPesquisa((prev) => ({ ...prev, [perguntaId]: valor }))
        }
        resposta={resposta}
        imovelCodigo={data.imovel?.codigo || 'PREVIEW'}
        onSubmit={handleSubmitPesquisa}
        showPreviewBanner
      />
    )
  }

  const confirmacaoData: PulseConfirmacaoData = {
    imovel: {
      codigo: data.imovel?.codigo ?? 'PREVIEW',
      titulo: data.imovel?.titulo,
      fotos: data.imovel?.fotos,
      localizacao: data.imovel?.localizacao
        ? {
            rua: data.imovel.localizacao.rua ?? undefined,
            numero: data.imovel.localizacao.numero ?? undefined,
            bairro: data.imovel.localizacao.bairro ?? undefined,
            cidade: data.imovel.localizacao.cidade ?? undefined,
          }
        : undefined,
    },
    contato: { nome: data.contato?.nome ?? '' },
    dashboard: {
      titulo: data.dashboard?.titulo ?? '',
      logo: data.dashboard?.logo ?? undefined,
    },
  }

  return (
    <ConfirmacaoStep
      data={confirmacaoData}
      onConfirmacao={handleConfirmacao}
      showPreviewBanner
    />
  )
}
